import * as React from "react";
import { Switch } from "../../switch";
import { Alert, AlertDescription, AlertTitle } from "../../alert";
import { Icon } from "../../icon";
import { cn } from "../../utils";
import { BackupButton } from "./backup-button";

/**
 * The shared read-only ↔ write switch used by the Table + SQL editors and the
 * Assistant. Read-only is the default everywhere (PRD §13.1–2). Flipping to
 * write mode surfaces the first-write backup nudge + the persistent
 * migration-drift warning (PRD §13.7–8) — both folded in here so every write
 * surface shows them consistently.
 */
export function WriteModeToggle({
  writeMode,
  onChange,
  className,
  showDriftBanner = true,
}: {
  writeMode: boolean;
  onChange: (next: boolean) => void;
  className?: string;
  showDriftBanner?: boolean;
}) {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex items-center gap-3">
        <Switch
          id="db-write-mode"
          checked={writeMode}
          onCheckedChange={onChange}
        />
        <label htmlFor="db-write-mode" className="flex items-center gap-1.5 type-body">
          <Icon
            name={writeMode ? "pencil" : "lock"}
            size="sm"
            className={writeMode ? "text-foreground" : "text-muted-foreground"}
          />
          <span className={writeMode ? "text-foreground" : "text-muted-foreground"}>
            {writeMode ? "Write mode" : "Read-only"}
          </span>
        </label>
      </div>

      {writeMode && showDriftBanner ? (
        <Alert>
          <Icon name="triangle-alert" />
          <AlertTitle>Write mode is on — changes are live.</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <span>
              Schema changes made here are live-only — they are not written to
              the migration source of truth
              (<code className="type-subtext">excellent/src/projects/migrations.ts</code>)
              and will not exist on a fresh install. Use Migrations → Export as
              SQL to capture them. Destructive operations require typed
              confirmation.
            </span>
            <BackupButton label="Back up before editing" />
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
